import React, { UIEvent, useCallback, useEffect, useMemo, useRef } from 'react';

import { Badge, Flex, Group, Text, px, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import dayjs from 'dayjs';
import useUncreditedReservations from 'hooks/reports/reservations/uncredited-reservations/useUncreditedReservations';
import { MantineReactTable, useMantineReactTable, type MRT_ColumnDef } from 'mantine-react-table';
import UncreditedReservationsDetailPanel from 'pages/private/owner/reports/UncreditedReservations/components/Content/UncreditedReservationsDetailPanel';
import { useNavigate } from 'react-router-dom';
import { UncreditedReservationsReport } from 'types';

import { useUncreditedReservationsContext } from '../../UncreditedReservationsContext';

interface UncreditedReservationsTableProps {
  headerHeight: number;
}

const UncreditedReservationsTable = ({ headerHeight }: UncreditedReservationsTableProps): JSX.Element => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { computedFilters } = useUncreditedReservationsContext();

  const { data, isLoading, isError, isFetching, fetchNextPage } = useUncreditedReservations({
    startDate: computedFilters.startDate,
    endDate: computedFilters.endDate,
    search: computedFilters.search,
    eventTypeIds: computedFilters.eventTypeIds,
  });

  const flatData = useMemo(() => data?.pages.flatMap(page => page.data.results) ?? [], [data]);

  const totalCount = data?.pages?.[0]?.data?.count ?? 0;
  const totalFetchedCount = flatData.length;

  const columns: MRT_ColumnDef<UncreditedReservationsReport>[] = [
    {
      accessorKey: 'athleteFullName',
      header: 'Athlete Name',
      Header: (
        <Text fs='italic' c='dimmed' pr='xs'>
          Athlete Name
        </Text>
      ),
      Cell: ({ row }) => (
        <Text
          fw='bold'
          fs='italic'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content' }}
          onClick={() => navigate(`/users/athletes/${row.original.athleteId}/general-info`)}
        >
          {row.original.athleteFullName}
        </Text>
      ),
      size: 200,
    },
    {
      accessorKey: 'activePackages',
      header: 'Current Packages',
      Header: (
        <Text fs='italic' c='dimmed' pr='xs'>
          Current Packages
        </Text>
      ),
      Cell: ({ cell }) => {
        const packages = cell.getValue<string[]>();
        return (
          <Text maw='30vw' truncate>
            {packages.length > 0 ? packages.join(', ') : 'None'}
          </Text>
        );
      },
      size: 250,
    },
    {
      accessorKey: 'soonestDate',
      header: 'Soonest Date',
      Header: (
        <Text fs='italic' c='dimmed' pr='xs'>
          Soonest Date
        </Text>
      ),
      Cell: ({ row }) => (
        <Group spacing={7}>
          <Text>{dayjs(row.original.soonestDate).format('MMM D, YYYY')}</Text>
          <Text c='dimmed'>{row.original.soonestTime}</Text>
        </Group>
      ),
      size: 200,
    },
    {
      accessorKey: 'uncreditedReservations',
      header: 'Uncredited Reservations',
      mantineTableHeadCellProps: {
        align: 'center',
      },
      Header: (
        <Text fs='italic' c='dimmed' ta='center' w='100%'>
          Uncredited Reservations
        </Text>
      ),
      Cell: ({ cell }) => {
        const count = cell.getValue<unknown[]>().length;
        return (
          <Flex justify='center'>
            <Badge color='red'>{count}</Badge>
          </Flex>
        );
      },
      size: 150,
    },
  ];

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (scrollHeight - scrollTop - clientHeight < 400 && !isFetching && totalFetchedCount < totalCount) {
          fetchNextPage();
        }
      }
    },
    [isFetching, totalFetchedCount, totalCount, fetchNextPage],
  );

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  const table = useMantineReactTable({
    columns,
    data: flatData,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableExpanding: true,
    enableExpandAll: false,
    enableFilters: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enablePagination: false,
    enableRowSelection: false,
    enableSorting: false,
    enableTopToolbar: false,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    mantineDetailPanelProps: props => {
      if (props.row.getIsExpanded()) {
        return {
          sx: {
            backgroundColor: theme.colors.gray[1],
          },
        };
      }
      return {};
    },
    mantineTableContainerProps: {
      ref: tableContainerRef,
      sx: { height: `calc(100vh - ${headerHeight + px(theme.spacing.lg) * 2 + (isMobile ? 120 : 0)}px)` },
      onScroll: (event: UIEvent<HTMLDivElement>) => fetchMoreOnBottomReached(event.target as HTMLDivElement),
    },
    mantineToolbarAlertBannerProps: {
      color: 'red',
      children: 'Error loading data',
    },
    displayColumnDefOptions: {
      'mrt-row-expand': { size: 10, Header: '' },
    },
    renderDetailPanel: ({ row }) => {
      if (row.getIsExpanded()) {
        return (
          <UncreditedReservationsDetailPanel
            athleteId={row.original.athleteId}
            uncreditedReservations={row.original.uncreditedReservations}
          />
        );
      }
      return <></>;
    },
    renderBottomToolbarCustomActions: () => (
      <Text fs='italic' c='dimmed' pl='lg'>
        Fetched {totalFetchedCount} of {totalCount} total rows
      </Text>
    ),
    initialState: { density: 'xs' },
    state: {
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching,
    },
  });

  return <MantineReactTable table={table} />;
};

export default UncreditedReservationsTable;
